import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmployerLayout } from '../JobPostPricing/EmployerLayout';
import { AssistantPage } from '../Applications/copilot/AssistantPage';
import OfferModal from '../Interview/OfferModal';
import { getApiUrl } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/Components/ui/dialog';
import { Label } from '@/Components/ui/label';
import { Textarea } from '@/Components/ui/textarea';
import { Input } from '@/Components/ui/input';
import {
  BriefcaseBusiness,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Loader2,
  Mail,
  PhoneCall,
  RefreshCw,
  ShieldCheck,
  UserCheck,
  Search,
  Users,
  Inbox,
  LayoutGrid,
  List as ListIcon,
  AlertTriangle,
  CalendarClock,
  Send,
  Briefcase,
  ChevronRight,
  Pencil,
  UserX,
  CalendarX,
  RotateCcw,
  FilePenLine,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';
import { OfferApprovalSwitch, WaitingApprovals, useOfferApprovals } from './OfferApprovalsPanel';

const DATE_OPTIONS = { year: 'numeric', month: 'short', day: 'numeric' };
const PIPELINE_STAGES = ['Selected', 'Offer Released', 'Offer Accepted', 'Background Verification', 'Hired', 'Joined'];
const TERMINAL_STAGES = ['Offer Rejected', 'Offer Expired', 'Background Verification Failed', 'Candidate Withdrawn', 'Did Not Join'];
// Stages where the joining date can still be edited and the candidate can be
// withdrawn from the workflow.
const JOINING_EDITABLE_STATUSES = ['Offer Released', 'Offer Accepted', 'Background Verification', 'Hired'];
const PIPELINE_SUMMARY_META = {
  Selected: { icon: CheckCircle2, iconWrap: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' },
  'Offer Released': { icon: Mail, iconWrap: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' },
  'Offer Accepted': { icon: CheckCircle2, iconWrap: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Background Verification': { icon: ShieldCheck, iconWrap: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  Hired: { icon: BriefcaseBusiness, iconWrap: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
  Joined: { icon: UserCheck, iconWrap: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500' },
  attention: { icon: CircleAlert, iconWrap: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
};

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString(undefined, DATE_OPTIONS);
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(undefined, { ...DATE_OPTIONS, hour: 'numeric', minute: '2-digit' });
}

function humanize(value) {
  return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// A value suitable for <input type="date"> (YYYY-MM-DD), or '' when unparseable.
function toDateInput(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const tz = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - tz).toISOString().slice(0, 10);
}

// Default new offer deadline when resending an expired offer: 7 days out.
function defaultReopenDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d;
}

function displayCandidateName(value) {
  const name = String(value || '').trim();
  if (!name || name !== name.toUpperCase()) return name;
  return name.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getCandidateStage(candidate) {
  const status = candidate.application_status || 'Selected';
  if (status === 'Background Verification' && candidate.background_verification_status === 'failed') {
    return 'Background Verification Failed';
  }
  return status;
}

function displayedStage(candidate) {
  const stage = getCandidateStage(candidate);
  if (stage === 'Selected') return 'Ready to Release Offer';
  if (stage === 'Background Verification' && candidate.background_verification_status === 'passed') return 'Ready to Hire';
  if (stage === 'Hired') return 'Ready to Join';
  return stage;
}

function stageMeta(candidate) {
  const stage = displayedStage(candidate);
  if (stage === 'Offer Rejected' || stage === 'Background Verification Failed' || stage === 'Candidate Withdrawn') {
    return { label: stage, className: 'bg-red-600 hover:bg-red-600 border-red-700 text-white', icon: CircleAlert };
  }
  if (stage === 'Offer Expired' || stage === 'Did Not Join') {
    return { label: stage, className: 'bg-amber-500 hover:bg-amber-500 border-amber-600 text-white', icon: Clock3 };
  }
  return { label: stage, className: 'border-green-200 bg-green-50 text-green-800 hover:bg-green-50', icon: CheckCircle2 };
}

function StatusBadge({ candidate }) {
  const { label, className, icon: Icon } = stageMeta(candidate);
  return <Badge className={`inline-flex h-8 w-fit self-start shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-0 text-[13px] font-semibold leading-none ${className}`}><Icon className="h-4 w-4" />{label}</Badge>;
}

function terminalMessage(candidate) {
  const stage = getCandidateStage(candidate);
  if (stage === 'Offer Rejected') return 'The candidate declined the offer.';
  if (stage === 'Offer Expired') return 'The offer expired before a response was recorded.';
  if (stage === 'Background Verification Failed') return 'Background verification did not pass. This hiring path is closed.';
  if (stage === 'Candidate Withdrawn') return 'The candidate withdrew or the offer was rescinded.';
  if (stage === 'Did Not Join') return 'The candidate did not join on the agreed date.';
  return '';
}

function roleIsClosed(candidate) {
  return !['', 'active', 'open'].includes(String(candidate.requirement_status || '').trim().toLowerCase());
}

// Classify a Hired candidate's joining date relative to today:
//   'overdue' — the joining date has passed but they aren't marked Joined yet
//   'week'    — joining within the next 7 days
//   'later'   — joining further out, or no date recorded
function joiningWindow(candidate) {
  const raw = candidate.joining_date ? new Date(candidate.joining_date) : null;
  if (!raw || Number.isNaN(raw.getTime())) return 'later';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const in7 = new Date(today); in7.setDate(in7.getDate() + 7);
  const day = new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  if (day < today) return 'overdue';
  if (day <= in7) return 'week';
  return 'later';
}

// Ordered pipeline for the per-candidate progress tracker. Short labels keep
// the inline stepper compact.
const STAGE_ORDER = ['Selected', 'Offer Released', 'Offer Accepted', 'Background Verification', 'Hired', 'Joined'];
const STAGE_SHORT = {
  Selected: 'Selected',
  'Offer Released': 'Offer',
  'Offer Accepted': 'Accepted',
  'Background Verification': 'BGV',
  Hired: 'Hired',
  Joined: 'Joined',
};

function stageIndex(candidate) {
  const stage = getCandidateStage(candidate);
  const idx = STAGE_ORDER.indexOf(stage);
  return idx; // -1 for terminal states
}

function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
}

// Colour cycle for candidate avatars — deterministic per name so a person
// keeps the same colour across renders.
const AVATAR_COLORS = [
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-indigo-100 text-indigo-700', 'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function Avatar({ name, size = 'md' }) {
  const dims = size === 'sm' ? 'h-9 w-9 text-xs' : 'h-11 w-11 text-sm';
  return (
    <span className={`flex ${dims} shrink-0 items-center justify-center rounded-full font-semibold ${avatarColor(name)}`}>
      {initialsFor(name)}
    </span>
  );
}

// Horizontal per-candidate pipeline tracker: filled dots up to the current
// stage, current one ringed, future stages muted. Terminal states render a
// red/amber interrupted marker so a stalled candidate reads at a glance.
function StageTracker({ candidate }) {
  const stage = getCandidateStage(candidate);
  const isTerminal = TERMINAL_STAGES.includes(stage);
  const current = stageIndex(candidate);

  return (
    <div className="flex items-center gap-0">
      {STAGE_ORDER.map((s, i) => {
        const done = !isTerminal && i < current;
        const isCurrent = !isTerminal && i === current;
        const dotClass = done
          ? 'bg-emerald-500 border-emerald-500'
          : isCurrent
            ? 'bg-white border-emerald-500 ring-2 ring-emerald-200'
            : 'bg-white border-slate-300';
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-1" title={s}>
              <span className={`h-3 w-3 rounded-full border-2 ${dotClass}`} />
              <span className={`hidden text-[10px] font-medium lg:block ${isCurrent ? 'text-emerald-700' : done ? 'text-slate-600' : 'text-slate-400'}`}>
                {STAGE_SHORT[s]}
              </span>
            </div>
            {i < STAGE_ORDER.length - 1 && (
              <span className={`h-0.5 w-6 shrink-0 lg:w-10 ${!isTerminal && i < current ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── BGV state helpers (shared by primary-action + full card) ───────────────
function bgvFlags(candidate) {
  const status = candidate.background_verification_status;
  return {
    passed: status === 'passed',
    inProgress: status === 'in_progress',
    needsStart: !status || status === 'pending',
  };
}

// The single most-important next step for a candidate. Inbox and Board both
// render this so the primary action stays identical to the List view. Returns
// null for waiting/terminal/complete states (no HR action right now).
function getPrimaryAction(candidate, handlers) {
  const status = candidate.application_status;
  const { passed, inProgress, needsStart } = bgvFlags(candidate);
  if (status === 'Selected') {
    return { key: 'offer', label: 'Release Offer', Icon: Mail, tone: 'primary', run: () => handlers.openOffer(candidate) };
  }
  if (status === 'Offer Released' && candidate.offer_status === 'released') {
    return { key: 'accept', label: 'Confirm Acceptance', Icon: PhoneCall, tone: 'outline', run: () => handlers.setManualAcceptance({ candidate, reason: '' }) };
  }
  if (status === 'Offer Accepted') {
    return { key: 'bgvstart', label: 'Start BGV', Icon: ShieldCheck, tone: 'primary', run: () => handlers.startBgv(candidate) };
  }
  if (status === 'Background Verification') {
    if (needsStart) return { key: 'bgvstart', label: 'Start BGV', Icon: ShieldCheck, tone: 'primary', run: () => handlers.startBgv(candidate) };
    if (inProgress) {
      return {
        key: 'bgv', label: 'Pass BGV', Icon: ShieldCheck, tone: 'primary', run: () => handlers.openConfirmation(candidate, {
          title: 'Mark background verification as passed?',
          description: `Confirm that ${candidate.candidate_name}'s background verification has passed. The candidate can then be confirmed as Hired.`,
          confirmLabel: 'Pass BGV',
          path: `/api/hiring-lifecycle/background-verification/${candidate.application_id}`,
          body: { status: 'passed' },
          successMessage: 'Background verification passed.',
        }),
      };
    }
    if (passed) {
      return {
        key: 'hire', label: 'Confirm Hired', Icon: BriefcaseBusiness, tone: 'primary', run: () => handlers.openConfirmation(candidate, {
          title: 'Confirm candidate as Hired?',
          description: `${candidate.candidate_name} passed BGV and will be recorded as Hired. Mark the candidate as Joined on their actual joining day.`,
          confirmLabel: 'Confirm Hired',
          path: `/api/hiring-lifecycle/hire/${candidate.application_id}`,
          body: null,
          successMessage: 'Candidate confirmed as Hired.',
        }),
      };
    }
  }
  if (status === 'Hired') {
    return {
      key: 'join', label: 'Mark as Joined', Icon: UserCheck, tone: 'primary', run: () => handlers.openConfirmation(candidate, {
        title: 'Mark candidate as Joined?',
        description: `${candidate.candidate_name}'s scheduled joining date is ${formatDate(candidate.joining_date)}. Confirm only after the candidate has actually joined.`,
        confirmLabel: 'Mark as Joined',
        path: `/api/hiring-lifecycle/join/${candidate.application_id}`,
        body: null,
        successMessage: 'Candidate marked as Joined.',
      }),
    };
  }
  return null;
}

// The permission behind each primary action, matching the route it calls.
const PRIMARY_ACTION_PERMISSION = {
  offer: 'offers.release',
  accept: 'offers.release',
  bgvstart: 'bgv.manage',
  bgv: 'bgv.manage',
  hire: 'onboarding.manage',
  join: 'onboarding.manage',
};

// With offer approvals on, drafting is a step of its own: whoever drafts offers can
// start one, and someone who can send offers releases it once it is approved.
function getAllowedPrimaryAction(candidate, handlers, can, offersNeedApproval = false) {
  const action = getPrimaryAction(candidate, handlers);
  if (!action) return null;
  if (action.key === 'offer' && offersNeedApproval && !can('offers.release')) {
    return can('offers.draft') ? { ...action, label: 'Prepare Offer', Icon: FilePenLine } : null;
  }
  return can(PRIMARY_ACTION_PERMISSION[action.key]) ? action : null;
}

// Which action-inbox bucket a candidate belongs to (null = not actionable now).
function inboxBucketKey(candidate) {
  const stage = getCandidateStage(candidate);
  if (TERMINAL_STAGES.includes(stage)) return 'attn';
  const status = candidate.application_status;
  const { passed, inProgress, needsStart } = bgvFlags(candidate);
  if (status === 'Selected') return 'offer';
  if (status === 'Offer Released' && candidate.offer_status === 'released') return 'accept';
  if (status === 'Offer Accepted') return 'bgvstart';
  if (status === 'Background Verification') {
    if (needsStart) return 'bgvstart';
    if (inProgress) return 'bgv';
    if (passed) return 'hire';
  }
  if (status === 'Hired') {
    const win = joiningWindow(candidate);
    if (win === 'overdue') return 'overdue';
    if (win === 'week') return 'joinweek';
    return 'joinlater';
  }
  return null;
}

const INBOX_BUCKETS = [
  { key: 'overdue', label: 'Overdue to join', Icon: AlertTriangle, accent: 'text-rose-600', wrap: 'bg-rose-50' },
  { key: 'attn', label: 'Needs attention', Icon: AlertTriangle, accent: 'text-rose-600', wrap: 'bg-rose-50' },
  { key: 'offer', label: 'Offers to release', Icon: Send, accent: 'text-emerald-600', wrap: 'bg-emerald-50' },
  { key: 'accept', label: 'Awaiting acceptance', Icon: Clock3, accent: 'text-violet-600', wrap: 'bg-violet-50' },
  { key: 'bgvstart', label: 'Ready to start BGV', Icon: ShieldCheck, accent: 'text-cyan-600', wrap: 'bg-cyan-50' },
  { key: 'bgv', label: 'BGV in progress', Icon: ShieldCheck, accent: 'text-amber-600', wrap: 'bg-amber-50' },
  { key: 'hire', label: 'Ready to hire', Icon: Briefcase, accent: 'text-indigo-600', wrap: 'bg-indigo-50' },
  { key: 'joinweek', label: 'Joining this week', Icon: CalendarClock, accent: 'text-teal-600', wrap: 'bg-teal-50' },
  { key: 'joinlater', label: 'Joining later', Icon: CalendarClock, accent: 'text-slate-500', wrap: 'bg-slate-50' },
];

// ── Full candidate card (List view) — the proven, complete card with every
// contextual action. Extracted verbatim so List behaviour is unchanged. ─────
function FullCandidateCard({ candidate, actionId, openOffer, openConfirmation, setManualAcceptance, setManualRoleClose, startBgv, setReschedule, setWithdraw, setReopen, setReviseConfirm, offersNeedApproval = false }) {
  const working = actionId.startsWith(`${candidate.application_id}:`);
  const { passed: bgvPassed, inProgress: bgvInProgress, needsStart: bgvNeedsStart } = bgvFlags(candidate);
  const workflowStage = getCandidateStage(candidate);
  const canEditJoining = JOINING_EDITABLE_STATUSES.includes(candidate.application_status);
  const canRevise = ['Offer Released', 'Offer Accepted'].includes(candidate.application_status);
  const isExpired = workflowStage === 'Offer Expired';
  const showJoiningDate = ['Offer Released', 'Offer Accepted', 'Background Verification', 'Hired', 'Joined'].includes(workflowStage);
  const showBgvDetails = ['Background Verification', 'Background Verification Failed', 'Hired', 'Joined'].includes(workflowStage);
  const showRejectedDate = workflowStage === 'Offer Rejected';
  const showExpiredDate = workflowStage === 'Offer Expired';
  const showAcceptedDate = workflowStage === 'Offer Accepted';
  const hasBgvActivity = Boolean(
    candidate.background_verification_started_at
    || candidate.background_verification_completed_at,
  );
  const terminalNotice = terminalMessage(candidate);
  const isRoleClosed = roleIsClosed(candidate);
  const { can } = useCompanyAccess();
  const canManageOnboarding = can('onboarding.manage');
  const canDraftOffer = can('offers.draft');
  const canReleaseOffer = can('offers.release');
  const canManageBgv = can('bgv.manage');
  const canAct = canManageOnboarding || canDraftOffer || canReleaseOffer || canManageBgv;
  return (
    <Card className="border-slate-200 transition hover:shadow-md">
      <CardHeader className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Identity block: avatar + name/role/contact */}
          <div className="flex min-w-0 items-start gap-3">
            <Avatar name={candidate.candidate_name} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base sm:text-lg">{displayCandidateName(candidate.candidate_name)}</CardTitle>
                <StatusBadge candidate={candidate} />
                {isRoleClosed && <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-700">Role closed</Badge>}
              </div>
              <p className="mt-0.5 text-sm font-medium text-foreground">{candidate.job_title || '-'}</p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                <span className="truncate">{candidate.candidate_email || '-'}</span>
                {candidate.job_location && <span>· {candidate.job_location}</span>}
                {candidate.candidate_phone && <span>· {candidate.candidate_phone}</span>}
              </p>
              {workflowStage === 'Offer Released' && candidate.joining_date && (
                <p className="mt-1 text-xs text-muted-foreground">Joining date: <span className="font-medium text-foreground">{formatDate(candidate.joining_date)}</span></p>
              )}
            </div>
          </div>

          {/* Right: stage tracker + Selected-stage quick actions */}
          <div className="flex flex-col items-end gap-3">
            <StageTracker candidate={candidate} />
            {candidate.application_status === 'Selected' && (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {canManageOnboarding && <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:border-red-400 hover:bg-red-50 hover:text-red-800" disabled={working} onClick={() => openConfirmation(candidate, {
                  title: 'Cancel candidate selection?',
                  description: `Remove ${displayCandidateName(candidate.candidate_name)} from Offer & Onboarding? The candidate will return to the application stage. No offer has been sent.`,
                  confirmLabel: 'Cancel Selection',
                  path: `/api/hiring-lifecycle/cancel-selection/${candidate.application_id}`,
                  body: null,
                  successMessage: 'Candidate selection cancelled. The candidate returned to the application stage.',
                })}>Cancel</Button>}
                {canReleaseOffer && <Button size="sm" disabled={working} onClick={() => openOffer(candidate)}><Mail className="h-4 w-4 mr-2" />Release Offer</Button>}
                {!canReleaseOffer && offersNeedApproval && canDraftOffer && <Button size="sm" disabled={working} onClick={() => openOffer(candidate)}><FilePenLine className="h-4 w-4 mr-2" />Prepare Offer</Button>}
                {working && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className={candidate.application_status === 'Selected' ? 'hidden' : 'space-y-4'}>
        {((showJoiningDate && workflowStage !== 'Offer Released') || showBgvDetails || showRejectedDate || showExpiredDate || showAcceptedDate) && (
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            {showJoiningDate && workflowStage !== 'Offer Released' && <div className="rounded-md border bg-muted/30 p-3"><div className="flex items-center justify-between gap-2"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Joining Date</p>{canEditJoining && <button type="button" disabled={working} onClick={() => setReschedule({ candidate, date: toDateInput(candidate.joining_date) })} className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline disabled:opacity-50"><Pencil className="h-3 w-3" />Edit</button>}</div><p className="mt-1 font-semibold text-foreground">{formatDate(candidate.joining_date)}</p></div>}
            {showBgvDetails && <div className="rounded-md border bg-muted/30 p-3"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">BGV Status</p><p className="mt-1 flex items-center gap-1.5 font-semibold text-foreground"><CheckCircle2 className="h-4 w-4 text-green-600" />{humanize(candidate.background_verification_status || 'Not started')}</p></div>}
            {showAcceptedDate && <div className="rounded-md border bg-muted/30 p-3"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Offer Accepted On</p><p className="mt-1 font-semibold text-foreground">{formatDate(candidate.offer_accepted_at)}</p></div>}
            {showRejectedDate && <div className="rounded-md border bg-muted/30 p-3"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Offer Rejected On</p><p className="mt-1 font-semibold text-foreground">{formatDate(candidate.offer_rejected_at)}</p></div>}
            {showExpiredDate && <div className="rounded-md border bg-muted/30 p-3"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Offer Expired On</p><p className="mt-1 font-semibold text-foreground">{formatDate(candidate.offer_expires_at)}</p></div>}
          </div>
        )}

        {terminalNotice ? (
          <div className={`flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 text-sm ${isExpired ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-800'}`}>
            <span>{terminalNotice}</span>
            {isExpired && canDraftOffer && (
              <Button size="sm" disabled={working} onClick={() => setReopen({ candidate, date: toDateInput(defaultReopenDate()) })}><RotateCcw className="h-4 w-4 mr-2" />Resend / extend offer</Button>
            )}
          </div>
        ) : candidate.application_status === 'Selected' ? (
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Next action</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button disabled={working} onClick={() => openOffer(candidate)}><Mail className="h-4 w-4 mr-2" />Release Offer</Button>
              {working && <span className="inline-flex items-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</span>}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 rounded-md border bg-muted/10 p-4 lg:flex-row lg:items-end lg:justify-between">
            {hasBgvActivity && (
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">BGV Activity</p>
                <div className="mt-2 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  {candidate.background_verification_started_at && <div><p className="text-muted-foreground">Started by HR</p><p className="mt-0.5 font-medium text-foreground">{formatDateTime(candidate.background_verification_started_at)}</p></div>}
                  {candidate.background_verification_completed_at && <div><p className="text-muted-foreground">{candidate.background_verification_status === 'passed' ? 'Marked as passed by HR' : 'Marked as failed by HR'}</p><p className="mt-0.5 font-medium text-foreground">{formatDateTime(candidate.background_verification_completed_at)}</p></div>}
                </div>
              </div>
            )}
            <div className={`min-w-fit ${hasBgvActivity ? 'lg:text-right' : 'lg:ml-auto lg:text-right'}`}>
              {canAct && <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Next action</p>}
              <div className="mt-2 flex flex-wrap gap-2 lg:justify-end">
              {candidate.application_status === 'Offer Released' && candidate.offer_status === 'released' && (
                <>
                  <span className="inline-flex items-center text-sm text-muted-foreground">Waiting for the candidate response.</span>
                  {canReleaseOffer && <Button variant="outline" disabled={working} onClick={() => setManualAcceptance({ candidate, reason: '' })}><PhoneCall className="h-4 w-4 mr-2" />Confirm Acceptance by Phone/Email</Button>}
                </>
              )}

              {candidate.application_status === 'Offer Accepted' && canManageBgv && (
                <Button disabled={working} onClick={() => startBgv(candidate)}><ShieldCheck className="h-4 w-4 mr-2" />Start BGV</Button>
              )}

              {candidate.application_status === 'Background Verification' && (
                <>
                  {canManageBgv && bgvNeedsStart && <Button variant="outline" disabled={working} onClick={() => startBgv(candidate)}><ShieldCheck className="h-4 w-4 mr-2" />Start BGV</Button>}
                  {canManageBgv && bgvInProgress && <Button disabled={working} onClick={() => openConfirmation(candidate, {
                    title: 'Mark background verification as passed?',
                    description: `Confirm that ${candidate.candidate_name}'s background verification has passed. The candidate can then be confirmed as Hired.`,
                    confirmLabel: 'Pass BGV',
                    path: `/api/hiring-lifecycle/background-verification/${candidate.application_id}`,
                    body: { status: 'passed' },
                    successMessage: 'Background verification passed.',
                  })}><ShieldCheck className="h-4 w-4 mr-2" />Pass BGV</Button>}
                  {canManageBgv && bgvInProgress && <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800" disabled={working} onClick={() => openConfirmation(candidate, {
                    title: 'Mark background verification as failed?',
                    description: `This will stop ${candidate.candidate_name}'s current hiring path. Confirm only after the verification result is final.`,
                    confirmLabel: 'Fail BGV',
                    destructive: true,
                    path: `/api/hiring-lifecycle/background-verification/${candidate.application_id}`,
                    body: { status: 'failed' },
                    successMessage: 'Background verification marked failed.',
                  })}>Fail BGV</Button>}
                  {canManageOnboarding && bgvPassed && <Button disabled={working} onClick={() => openConfirmation(candidate, {
                    title: 'Confirm candidate as Hired?',
                    description: `${candidate.candidate_name} passed BGV and will be recorded as Hired. Mark the candidate as Joined on their actual joining day.`,
                    confirmLabel: 'Confirm Hired',
                    path: `/api/hiring-lifecycle/hire/${candidate.application_id}`,
                    body: null,
                    successMessage: 'Candidate confirmed as Hired.',
                  })}><BriefcaseBusiness className="h-4 w-4 mr-2" />Confirm Hired</Button>}
                </>
              )}

              {candidate.application_status === 'Hired' && canManageOnboarding && (
                <Button disabled={working} onClick={() => openConfirmation(candidate, {
                  title: 'Mark candidate as Joined?',
                  description: `${candidate.candidate_name}'s scheduled joining date is ${formatDate(candidate.joining_date)}. Confirm only after the candidate has actually joined.`,
                  confirmLabel: 'Mark as Joined',
                  path: `/api/hiring-lifecycle/join/${candidate.application_id}`,
                  body: null,
                  successMessage: 'Candidate marked as Joined.',
                })}><UserCheck className="h-4 w-4 mr-2" />Mark as Joined</Button>
              )}
              {candidate.application_status === 'Joined' && !isRoleClosed && can('jobs.close') && (
                <Button variant="outline" className="border-red-300 text-red-700 hover:border-red-400 hover:bg-red-50 hover:text-red-800" disabled={working} onClick={() => setManualRoleClose({ candidate, reason: 'Position filled' })}>Close Role</Button>
              )}
              {working && <span className="inline-flex items-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</span>}
              </div>
            </div>
          </div>
        )}

        {/* Escape hatches — the things that go sideways in real onboarding:
            reschedule the joining date, withdraw the candidate, or record a
            no-show. Kept muted and separate from the primary next action. */}
        {canEditJoining && canManageOnboarding && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-3 text-xs">
            <button type="button" disabled={working} onClick={() => setReschedule({ candidate, date: toDateInput(candidate.joining_date) })} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-50"><Pencil className="h-3.5 w-3.5" />Reschedule joining date</button>
            {canRevise && canDraftOffer && (
              <button type="button" disabled={working} onClick={() => setReviseConfirm({ candidate })} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-50"><FilePenLine className="h-3.5 w-3.5" />Revise offer</button>
            )}
            <button type="button" disabled={working} onClick={() => setWithdraw({ candidate, reason: '', outcome: 'withdrawn' })} className="inline-flex items-center gap-1 text-muted-foreground hover:text-red-700 disabled:opacity-50"><UserX className="h-3.5 w-3.5" />{candidate.application_status === 'Offer Released' ? 'Withdraw offer' : 'Candidate withdrew'}</button>
            {candidate.application_status === 'Hired' && (
              <button type="button" disabled={working} onClick={() => setWithdraw({ candidate, reason: '', outcome: 'did_not_join' })} className="inline-flex items-center gap-1 text-muted-foreground hover:text-amber-700 disabled:opacity-50"><CalendarX className="h-3.5 w-3.5" />Did not join</button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Job ordering: newest JD first (job ids are random, so sort by created_at).
function parseDate(value) {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d : null;
}
function byNewestJob(a, b) {
  const da = parseDate(a.created);
  const db = parseDate(b.created);
  if (da && db) return db - da;
  if (da) return -1;
  if (db) return 1;
  return a.title.localeCompare(b.title);
}
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
function postedLabel(value) {
  const d = parseDate(value);
  if (!d) return null;
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Posted today';
  if (days === 1) return 'Posted yesterday';
  if (days < 7) return `Posted ${days}d ago`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `Posted ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })}`;
}
function isNewJob(value) {
  const d = parseDate(value);
  return Boolean(d) && Date.now() - d.getTime() < WEEK_MS;
}

// Lower-case and collapse whitespace so "  aman   kumar " matches "AMAN KUMAR".
function norm(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// A search box with a clear (×) button; Esc clears it.
function SearchBox({ value, onChange, placeholder, inputRef, className, shortcut }) {
  return (
    <div className={`relative ${className || ''}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange(''); } }}
        placeholder={placeholder}
        className={`pl-9 ${value || shortcut ? 'pr-9' : ''}`}
      />
      {value ? (
        <button type="button" onClick={() => { onChange(''); inputRef?.current?.focus(); }} aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      ) : shortcut ? (
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 text-[10px] font-medium text-slate-400 sm:block">{shortcut}</kbd>
      ) : null}
    </div>
  );
}

export default function HiringLifecycle() {
  const { can } = useCompanyAccess();
  const offerApprovals = useOfferApprovals();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState('');
  const [view, setView] = useState('inbox'); // 'inbox' | 'board' | 'list'
  const [selectedJob, setSelectedJob] = useState('all'); // job_id or 'all'
  const [quickFilter, setQuickFilter] = useState('all'); // all|offer|join|bgv|attn
  const [searchQuery, setSearchQuery] = useState('');
  const [jobQuery, setJobQuery] = useState('');
  const searchRef = useRef(null);
  const [offerCandidate, setOfferCandidate] = useState(null);
  const [offerPrefill, setOfferPrefill] = useState(null); // previous snapshot when revising
  const [reviseConfirm, setReviseConfirm] = useState(null); // { candidate }
  const [manualAcceptance, setManualAcceptance] = useState(null);
  const [manualRoleClose, setManualRoleClose] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [reschedule, setReschedule] = useState(null); // { candidate, date }
  const [withdraw, setWithdraw] = useState(null); // { candidate, reason, outcome }
  const [reopen, setReopen] = useState(null); // { candidate, date }

  const loadCandidates = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/hiring-lifecycle/candidates'), {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load the hiring pipeline.');
      setCandidates(data.candidates || []);
    } catch (error) {
      if (!silent) toast.error(error.message || 'Could not load the hiring pipeline.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      if (!document.hidden && !actionId) loadCandidates({ silent: true });
    }, 20000);
    return () => window.clearInterval(interval);
  }, [actionId, loadCandidates]);

  const runAction = async (candidate, path, body, successMessage) => {
    const nextActionId = `${candidate.application_id}:${path}`;
    setActionId(nextActionId);
    try {
      const response = await fetch(getApiUrl(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'The update could not be saved.');
      toast.success(data.message || successMessage || 'Updated.');
      await loadCandidates({ silent: true });
      return true;
    } catch (error) {
      toast.error(error.message || 'The update could not be saved.');
      return false;
    } finally {
      setActionId('');
    }
  };

  const openOffer = (candidate) => {
    setOfferCandidate({
      applicationId: candidate.application_id,
      id: candidate.candidate_email,
      name: candidate.candidate_name,
      email: candidate.candidate_email,
      location: candidate.job_location || candidate.candidate_location,
      jobId: candidate.job_id,
      position: candidate.job_title,
      job_type: 'Full-time',
    });
  };

  const openConfirmation = (candidate, options) => setConfirmation({ candidate, ...options });

  const submitConfirmation = async () => {
    if (!confirmation) return;
    const saved = await runAction(
      confirmation.candidate,
      confirmation.path,
      confirmation.body,
      confirmation.successMessage,
    );
    if (saved) setConfirmation(null);
  };

  const submitManualAcceptance = async () => {
    const reason = manualAcceptance?.reason?.trim();
    if (!reason) {
      toast.error('Enter the candidate acceptance confirmation details.');
      return;
    }
    const saved = await runAction(
      manualAcceptance.candidate,
      `/api/hiring-lifecycle/offers/${manualAcceptance.candidate.application_id}/confirm-acceptance-by-phone-email`,
      { confirmation_reason: reason },
      'Candidate acceptance confirmed by phone/email.',
    );
    if (saved) setManualAcceptance(null);
  };

  const submitManualRoleClose = async () => {
    const reason = manualRoleClose?.reason?.trim();
    if (!reason) {
      toast.error('Enter a reason for closing the role.');
      return;
    }
    const saved = await runAction(
      manualRoleClose.candidate,
      `/api/hiring-lifecycle/roles/${manualRoleClose.candidate.job_id}/close`,
      { reason },
      'Role closed.',
    );
    if (saved) setManualRoleClose(null);
  };

  const startBgv = (candidate) => runAction(
    candidate,
    `/api/hiring-lifecycle/background-verification/${candidate.application_id}`,
    { status: 'in_progress' },
    'Background verification started.',
  );

  const submitReschedule = async () => {
    const date = reschedule?.date;
    if (!date) { toast.error('Pick a new joining date.'); return; }
    const saved = await runAction(
      reschedule.candidate,
      `/api/hiring-lifecycle/applications/${reschedule.candidate.application_id}/joining-date`,
      { joining_date: date },
      'Joining date updated.',
    );
    if (saved) setReschedule(null);
  };

  const submitWithdraw = async () => {
    const reason = withdraw?.reason?.trim();
    if (!reason) { toast.error('Enter a reason.'); return; }
    const saved = await runAction(
      withdraw.candidate,
      `/api/hiring-lifecycle/applications/${withdraw.candidate.application_id}/withdraw`,
      { reason, outcome: withdraw.outcome || 'withdrawn' },
      withdraw.outcome === 'did_not_join' ? 'Candidate marked as Did Not Join.' : 'Candidate withdrawn.',
    );
    if (saved) setWithdraw(null);
  };

  const submitReopen = async () => {
    const date = reopen?.date;
    if (!date) { toast.error('Pick a new offer deadline.'); return; }
    const saved = await runAction(
      reopen.candidate,
      `/api/hiring-lifecycle/applications/${reopen.candidate.application_id}/reopen-offer`,
      { expiry_date: date },
      'Offer resent with a new deadline.',
    );
    if (saved) setReopen(null);
  };

  // Revise: reopen the offer (server voids it → Selected), then open the editor
  // pre-filled with the previous snapshot so HR only tweaks what changed.
  const submitRevision = async () => {
    const candidate = reviseConfirm?.candidate;
    if (!candidate) return;
    setActionId(`${candidate.application_id}:revise`);
    try {
      const response = await fetch(
        getApiUrl(`/api/hiring-lifecycle/applications/${candidate.application_id}/revise-offer`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not reopen the offer for revision.');
      setReviseConfirm(null);
      await loadCandidates({ silent: true });
      setOfferPrefill(data.previous_offer || null);
      openOffer(candidate);
      toast.success('Offer reopened for revision. Update the details and release the revised offer.');
    } catch (error) {
      toast.error(error.message || 'Could not reopen the offer for revision.');
    } finally {
      setActionId('');
    }
  };

  const handlers = { openOffer, openConfirmation, setManualAcceptance, setManualRoleClose, startBgv, setReschedule, setWithdraw, setReopen, setReviseConfirm };
  const offersNeedApproval = offerApprovals.policy.needsApproval;
  const cardProps = { actionId, openOffer, openConfirmation, setManualAcceptance, setManualRoleClose, startBgv, setReschedule, setWithdraw, setReopen, setReviseConfirm, offersNeedApproval };

  // ── Jobs (JD rail) — unique jobs with their pipeline counts ──────────────
  const jobs = useMemo(() => {
    const map = new Map();
    candidates.forEach((c) => {
      const id = String(c.job_id ?? c.job_title ?? 'unknown');
      if (!map.has(id)) map.set(id, { id, title: c.job_title || 'Untitled role', created: c.job_created_at || null, count: 0 });
      map.get(id).count += 1;
    });
    return [...map.values()].sort(byNewestJob);
  }, [candidates]);

  const jq = norm(jobQuery);
  const filteredJobs = useMemo(
    () => (jq ? jobs.filter((j) => norm(j.title).includes(jq)) : jobs),
    [jobs, jq],
  );

  // Candidate search is global: while a query is typed it spans every job,
  // whatever is selected in the rail (the placeholder promises "across all
  // jobs"). Clearing it returns to the selected job.
  const q = norm(searchQuery);
  const searching = Boolean(q);
  const effectiveJob = searching ? 'all' : selectedJob;
  const scoped = useMemo(() => candidates.filter((c) => {
    if (effectiveJob !== 'all' && String(c.job_id ?? c.job_title ?? 'unknown') !== effectiveJob) return false;
    if (q && ![displayCandidateName(c.candidate_name), c.candidate_email, c.job_title, c.job_location, c.candidate_location]
      .some((v) => norm(v).includes(q))) return false;
    return true;
  }), [candidates, effectiveJob, q]);

  // Searching means "find this person"; the Action Inbox only lists people who
  // need action, so a typed search moves to List where everyone is visible.
  const onSearchChange = (value) => {
    setSearchQuery(value);
    if (norm(value) && view === 'inbox') setView('list');
  };
  const selectJob = (id) => { setSelectedJob(id); setSearchQuery(''); };
  const resetAll = () => { setSelectedJob('all'); setQuickFilter('all'); setSearchQuery(''); setJobQuery(''); };

  // "/" jumps to the candidate search from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      if (document.querySelector('[role="dialog"]')) return; // don't steal focus from an open dialog
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const matchesQuickFilter = (c) => {
    if (quickFilter === 'all') return true;
    if (quickFilter === 'offer') return c.application_status === 'Selected';
    if (quickFilter === 'join') return c.application_status === 'Hired';
    if (quickFilter === 'bgv') return c.application_status === 'Background Verification';
    if (quickFilter === 'attn') return TERMINAL_STAGES.includes(getCandidateStage(c));
    return true;
  };
  const filtered = scoped.filter(matchesQuickFilter);

  const quickCounts = useMemo(() => ({
    all: scoped.length,
    offer: scoped.filter((c) => c.application_status === 'Selected').length,
    join: scoped.filter((c) => c.application_status === 'Hired').length,
    bgv: scoped.filter((c) => c.application_status === 'Background Verification').length,
    attn: scoped.filter((c) => TERMINAL_STAGES.includes(getCandidateStage(c))).length,
  }), [scoped]);

  // Inbox actionable count (across current scope, ignoring quickFilter) — the
  // red badge on the Action Inbox tab.
  const inboxActionable = useMemo(() => scoped.filter((c) => inboxBucketKey(c) !== null), [scoped]);

  // Group actionable candidates into inbox buckets (respecting quickFilter).
  const inboxGroups = useMemo(() => {
    const src = quickFilter === 'all' ? scoped : filtered;
    const groups = {};
    src.forEach((c) => {
      const key = inboxBucketKey(c);
      if (!key) return;
      (groups[key] = groups[key] || []).push(c);
    });
    return groups;
  }, [scoped, filtered, quickFilter]);

  const sortedList = useMemo(() => [...filtered].sort((a, b) => {
    const ai = stageIndex(a); const bi = stageIndex(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  }), [filtered]);

  const jumpToCandidate = (candidate) => {
    setView('list');
    setQuickFilter('all');
    setSearchQuery(candidate.candidate_email || candidate.candidate_name || '');
  };

  const VIEW_TABS = [
    { key: 'inbox', label: 'Action Inbox', Icon: Inbox },
    { key: 'board', label: 'Board', Icon: LayoutGrid },
    { key: 'list', label: 'List', Icon: ListIcon },
  ];

  const QUICK_CHIPS = [
    { key: 'all', label: 'All' },
    { key: 'offer', label: 'Offer to release', Icon: Mail },
    { key: 'join', label: 'Joining', Icon: CalendarClock },
    { key: 'bgv', label: 'BGV', Icon: ShieldCheck },
    { key: 'attn', label: 'Needs attention', Icon: AlertTriangle },
  ];

  const renderInbox = () => {
    const buckets = INBOX_BUCKETS.map((b) => ({ ...b, items: inboxGroups[b.key] || [] })).filter((b) => b.items.length);
    if (!buckets.length) {
      return (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-7 w-7" /></span>
            <div>
              <p className="font-semibold text-foreground">You&apos;re all caught up</p>
              <p className="mt-1 text-sm text-muted-foreground">Nothing needs your action right now. New steps will appear here as candidates progress.</p>
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4">
        {buckets.map((bucket) => {
          const { Icon } = bucket;
          return (
            <div key={bucket.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className={`flex items-center gap-2 px-4 py-2.5 ${bucket.wrap}`}>
                <Icon className={`h-4 w-4 ${bucket.accent}`} />
                <span className="text-sm font-semibold text-slate-800">{bucket.label}</span>
                <span className={`ml-1 rounded-full bg-white px-2 text-xs font-semibold ${bucket.accent}`}>{bucket.items.length}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {bucket.items.map((candidate) => {
                  const working = actionId.startsWith(`${candidate.application_id}:`);
                  const primary = getAllowedPrimaryAction(candidate, handlers, can, offersNeedApproval);
                  const isAttn = bucket.key === 'attn';
                  return (
                    <div key={candidate.application_id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <Avatar name={candidate.candidate_name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">{displayCandidateName(candidate.candidate_name)}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {candidate.job_title || '-'}
                          {isAttn && terminalMessage(candidate) && <span className="text-rose-600"> · {terminalMessage(candidate)}</span>}
                          {bucket.key === 'overdue' && <span className="font-medium text-rose-600"> · Was due {formatDate(candidate.joining_date)}</span>}
                          {bucket.key === 'joinweek' && candidate.joining_date && <span className="font-medium text-teal-600"> · Joins {formatDate(candidate.joining_date)}</span>}
                          {bucket.key === 'joinlater' && candidate.joining_date && <span> · Joins {formatDate(candidate.joining_date)}</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {bucket.key === 'offer' && can('onboarding.manage') && (
                          <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:border-red-400 hover:bg-red-50 hover:text-red-800" disabled={working} onClick={() => openConfirmation(candidate, {
                            title: 'Cancel candidate selection?',
                            description: `Remove ${displayCandidateName(candidate.candidate_name)} from Offer & Onboarding? The candidate will return to the application stage. No offer has been sent.`,
                            confirmLabel: 'Cancel Selection',
                            path: `/api/hiring-lifecycle/cancel-selection/${candidate.application_id}`,
                            body: null,
                            successMessage: 'Candidate selection cancelled. The candidate returned to the application stage.',
                          })}>Cancel</Button>
                        )}
                        {bucket.key === 'overdue' && can('onboarding.manage') && (
                          <Button variant="outline" size="sm" disabled={working} onClick={() => setReschedule({ candidate, date: toDateInput(candidate.joining_date) })}><Pencil className="h-3.5 w-3.5 mr-1.5" />Reschedule</Button>
                        )}
                        {isAttn ? (
                          getCandidateStage(candidate) === 'Offer Expired' && can('offers.draft') ? (
                            <Button size="sm" disabled={working} onClick={() => setReopen({ candidate, date: toDateInput(defaultReopenDate()) })}><RotateCcw className="h-4 w-4 mr-2" />Resend offer</Button>
                          ) : (
                            <Button variant="outline" size="sm" onClick={() => jumpToCandidate(candidate)}>View</Button>
                          )
                        ) : primary ? (
                          <Button size="sm" variant={primary.tone === 'outline' ? 'outline' : 'default'} disabled={working} onClick={primary.run}>
                            {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <primary.Icon className="h-4 w-4 mr-2" />}{primary.label}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderBoard = () => {
    const columns = PIPELINE_STAGES.map((stage) => ({
      stage,
      label: stage === 'Background Verification' ? 'BGV' : stage,
      meta: PIPELINE_SUMMARY_META[stage],
      items: filtered.filter((c) => getCandidateStage(c) === stage),
    }));
    const attnItems = filtered.filter((c) => TERMINAL_STAGES.includes(getCandidateStage(c)));
    if (attnItems.length) {
      columns.push({ stage: 'attention', label: 'Attention', meta: PIPELINE_SUMMARY_META.attention, items: attnItems });
    }
    return (
      <div className="flex gap-3 overflow-x-auto pb-3">
        {columns.map((col) => (
          <div key={col.stage} className="flex max-h-[70vh] w-[240px] shrink-0 flex-col rounded-xl bg-slate-50">
            <div className="flex items-center gap-2 px-3 pb-2 pt-3">
              <span className={`h-2 w-2 rounded-full ${col.meta.dot}`} />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{col.label}</span>
              <span className="ml-auto rounded-full bg-white px-2 text-[11px] font-semibold text-slate-500">{col.items.length}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
              {col.items.length === 0 ? (
                <p className="py-3 text-center text-[11px] text-slate-400">—</p>
              ) : col.items.map((candidate) => {
                const working = actionId.startsWith(`${candidate.application_id}:`);
                const primary = getAllowedPrimaryAction(candidate, handlers, can, offersNeedApproval);
                const isRoleClosed = roleIsClosed(candidate);
                const terminal = TERMINAL_STAGES.includes(getCandidateStage(candidate));
                return (
                  <div key={candidate.application_id} className="rounded-lg border border-slate-200 bg-white p-2.5">
                    <div className="flex items-center gap-2">
                      <Avatar name={candidate.candidate_name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold text-foreground">{displayCandidateName(candidate.candidate_name)}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{candidate.job_title || '-'}</p>
                      </div>
                    </div>
                    {isRoleClosed && <span className="mt-1.5 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">Role closed</span>}
                    {candidate.joining_date && ['Hired', 'Background Verification', 'Offer Accepted'].includes(candidate.application_status) && (
                      candidate.application_status === 'Hired' && joiningWindow(candidate) === 'overdue' ? (
                        <p className="mt-1.5 text-[11px] font-medium text-rose-600"><AlertTriangle className="mr-1 inline h-3 w-3" />Overdue — was due {formatDate(candidate.joining_date)}</p>
                      ) : (
                        <p className="mt-1.5 text-[11px] text-muted-foreground"><CalendarClock className="mr-1 inline h-3 w-3" />Joins {formatDate(candidate.joining_date)}</p>
                      )
                    )}
                    {terminal && <p className="mt-1.5 text-[11px] text-rose-600">{terminalMessage(candidate)}</p>}
                    {primary && (
                      <Button size="sm" variant={primary.tone === 'outline' ? 'outline' : 'default'} className="mt-2 h-7 w-full text-[12px]" disabled={working} onClick={primary.run}>
                        {working ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <primary.Icon className="h-3.5 w-3.5 mr-1.5" />}{primary.label}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderList = () => {
    if (sortedList.length === 0) {
      return (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center">
            <p className="font-medium text-foreground">No candidates match</p>
            <p className="mt-1 text-sm text-muted-foreground">{q ? 'Try a different search term' : 'No candidates in this view'}.</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-3">
        {sortedList.map((candidate) => (
          <FullCandidateCard key={candidate.application_id} candidate={candidate} {...cardProps} />
        ))}
      </div>
    );
  };

  const scopeLabel = effectiveJob === 'all' ? 'All jobs' : (jobs.find((j) => j.id === effectiveJob)?.title || 'Job');
  const resultCount = view === 'inbox'
    ? Object.values(inboxGroups).reduce((total, group) => total + group.length, 0)
    : filtered.length;

  return (
    <EmployerLayout>
      {/* AI Assistant: publish the pipeline rows on screen (G2 page adapter) */}
      <AssistantPage page="hiring-lifecycle" candidates={candidates} />
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-2 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Offer &amp; Onboarding</h1>
            <p className="text-sm text-muted-foreground mt-1">Track every selected candidate from offer through to joining.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <OfferApprovalSwitch policy={offerApprovals.policy} onChange={offerApprovals.setNeedsApproval} />
            <Button variant="outline" onClick={() => { loadCandidates(); offerApprovals.reload(); }} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </div>

        <WaitingApprovals
          items={offerApprovals.waiting}
          onDecided={offerApprovals.reload}
          displayName={displayCandidateName}
          formatDate={formatDate}
        />

        {/* View tabs + global search */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1 rounded-xl bg-slate-100 p-1">
            {VIEW_TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  view === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="h-4 w-4" />{label}
                {key === 'inbox' && inboxActionable.length > 0 && (
                  <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">{inboxActionable.length}</span>
                )}
              </button>
            ))}
          </div>
          <SearchBox
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search any candidate across all jobs…"
            inputRef={searchRef}
            shortcut="/"
            className="w-full sm:w-80"
          />
        </div>

        {/* Mobile job selector (rail is desktop-only) */}
        <div className="lg:hidden">
          <select
            value={effectiveJob}
            onChange={(e) => selectJob(e.target.value)}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All jobs ({candidates.length})</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title} ({j.count})</option>)}
          </select>
        </div>

        <div className="flex gap-5 items-start">
          {/* Left rail: Jobs */}
          <aside className="hidden lg:block w-60 shrink-0 border-r border-slate-200 pr-4">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Jobs <span className="font-normal normal-case tracking-normal text-slate-400">· newest first</span>
            </p>
            {jobs.length > 5 && (
              <SearchBox value={jobQuery} onChange={setJobQuery} placeholder="Search jobs…" className="mb-2 [&_input]:h-8 [&_input]:text-xs" />
            )}
            <button
              type="button"
              onClick={() => selectJob('all')}
              className={`mb-1 w-full rounded-lg px-3 py-2 text-left transition ${effectiveJob === 'all' ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-slate-800">All jobs</span>
                <span className="text-xs text-slate-500">{candidates.length}</span>
              </div>
              <p className="text-[10px] text-slate-400">{searching ? 'Searching all jobs' : 'Everything in pipeline'}</p>
            </button>
            <div className="max-h-[60vh] space-y-0.5 overflow-y-auto">
              {filteredJobs.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-slate-400">No jobs match “{jobQuery.trim()}”</p>
              ) : filteredJobs.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  onClick={() => selectJob(j.id)}
                  title={j.title}
                  className={`w-full rounded-lg px-3 py-2 text-left transition ${effectiveJob === j.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="line-clamp-2 text-[13px] font-medium leading-snug text-slate-700">{j.title}</span>
                    <span className="shrink-0 pt-0.5 text-xs text-slate-500">{j.count}</span>
                  </div>
                  {postedLabel(j.created) && (
                    <p className={`mt-0.5 text-[10px] ${isNewJob(j.created) ? 'font-semibold text-emerald-600' : 'text-slate-400'}`}>{postedLabel(j.created)}</p>
                  )}
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Filter chips */}
            <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
              {QUICK_CHIPS.map(({ key, label, Icon }) => {
                const active = quickFilter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setQuickFilter(key)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      active ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" />}{label}
                    <span className={`rounded-full px-1.5 text-[10px] font-semibold ${active ? 'bg-white text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{quickCounts[key]}</span>
                  </button>
                );
              })}
            </div>

            {/* Scope line */}
            <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
              {searching ? (
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground">{resultCount}</span> result{resultCount !== 1 ? 's' : ''} for{' '}
                  <span className="font-semibold text-foreground">“{searchQuery.trim()}”</span> across all jobs
                </span>
              ) : (
                <>
                  <span className="font-semibold text-foreground">{scopeLabel}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                  <span className="text-muted-foreground">{view === 'inbox' ? 'Action inbox' : view === 'board' ? 'Pipeline board' : 'All candidates'}</span>
                </>
              )}
              {(selectedJob !== 'all' || quickFilter !== 'all' || searching || jobQuery) && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="ml-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>

            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
            ) : candidates.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Users className="h-7 w-7" /></span>
                  <div>
                    <p className="font-semibold text-foreground">No one in the hiring workflow yet</p>
                    <p className="mt-1 text-sm text-muted-foreground">Select a candidate from an application to start the offer-to-joining process.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {view === 'inbox' && renderInbox()}
                {view === 'board' && renderBoard()}
                {view === 'list' && renderList()}
              </>
            )}
          </div>
        </div>
      </div>

      <OfferModal
        open={Boolean(offerCandidate)}
        candidate={offerCandidate}
        prefill={offerPrefill}
        onReleased={async () => { setOfferCandidate(null); setOfferPrefill(null); await loadCandidates({ silent: true }); }}
        onClose={() => { setOfferCandidate(null); setOfferPrefill(null); offerApprovals.reload(); }}
      />

      <Dialog open={Boolean(manualAcceptance)} onOpenChange={(open) => { if (!open) setManualAcceptance(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Acceptance by Phone/Email</DialogTitle>
            <DialogDescription>Record how {manualAcceptance?.candidate?.candidate_name || 'the candidate'} accepted the offer when they could not use the email link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="acceptance-confirmation">Confirmation details</Label>
            <Textarea id="acceptance-confirmation" value={manualAcceptance?.reason || ''} onChange={(event) => setManualAcceptance((current) => ({ ...current, reason: event.target.value }))} placeholder="For example: Confirmed by phone on 29 Jul 2026 with the candidate." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualAcceptance(null)}>Cancel</Button>
            <Button onClick={submitManualAcceptance} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Confirm Acceptance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(manualRoleClose)} onOpenChange={(open) => { if (!open) setManualRoleClose(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Role</DialogTitle>
            <DialogDescription>Close {manualRoleClose?.candidate?.job_title || 'this role'} manually. This does not change candidate records already in the workflow.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="role-closure-reason">Closure reason</Label>
            <Textarea id="role-closure-reason" value={manualRoleClose?.reason || ''} onChange={(event) => setManualRoleClose((current) => ({ ...current, reason: event.target.value }))} placeholder="For example: Position filled, hiring cancelled, or role put on hold." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualRoleClose(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitManualRoleClose} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Close Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmation)} onOpenChange={(open) => { if (!open) setConfirmation(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmation?.title}</DialogTitle>
            <DialogDescription>{confirmation?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmation(null)}>Cancel</Button>
            <Button className={confirmation?.destructive ? 'bg-red-600 hover:bg-red-700' : ''} onClick={submitConfirmation} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{confirmation?.confirmLabel}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule joining date */}
      <Dialog open={Boolean(reschedule)} onOpenChange={(open) => { if (!open) setReschedule(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change joining date</DialogTitle>
            <DialogDescription>
              Update the joining date for {displayCandidateName(reschedule?.candidate?.candidate_name) || 'this candidate'}
              {reschedule?.candidate?.joining_date ? ` (currently ${formatDate(reschedule.candidate.joining_date)})` : ''}. This updates the date on their offer record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reschedule-date">New joining date</Label>
            <Input id="reschedule-date" type="date" value={reschedule?.date || ''} onChange={(event) => setReschedule((current) => ({ ...current, date: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedule(null)}>Cancel</Button>
            <Button onClick={submitReschedule} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save date</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw / Did not join */}
      <Dialog open={Boolean(withdraw)} onOpenChange={(open) => { if (!open) setWithdraw(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{withdraw?.outcome === 'did_not_join' ? 'Mark as Did Not Join' : 'Withdraw candidate'}</DialogTitle>
            <DialogDescription>
              {withdraw?.outcome === 'did_not_join'
                ? `Record that ${displayCandidateName(withdraw?.candidate?.candidate_name) || 'this candidate'} did not join on the agreed date. This removes them from the active workflow.`
                : `Remove ${displayCandidateName(withdraw?.candidate?.candidate_name) || 'this candidate'} from the hiring workflow (candidate backed out, or the offer is being rescinded). Any live offer is voided so it can't be accepted.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="withdraw-reason">Reason</Label>
            <Textarea id="withdraw-reason" value={withdraw?.reason || ''} onChange={(event) => setWithdraw((current) => ({ ...current, reason: event.target.value }))} placeholder={withdraw?.outcome === 'did_not_join' ? 'For example: Did not show up on the joining date; unreachable since.' : 'For example: Candidate accepted another offer.'} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdraw(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitWithdraw} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{withdraw?.outcome === 'did_not_join' ? 'Mark Did Not Join' : 'Withdraw candidate'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revise offer confirmation */}
      <Dialog open={Boolean(reviseConfirm)} onOpenChange={(open) => { if (!open && !actionId) setReviseConfirm(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revise this offer?</DialogTitle>
            <DialogDescription>
              This reopens the offer editor for {displayCandidateName(reviseConfirm?.candidate?.candidate_name) || 'this candidate'}, pre-filled with the current offer. The existing offer is <span className="font-medium">voided</span>, and once you release the revised offer the candidate must accept it again. Releasing a revised offer generates a new offer letter (and uses offer-letter credits).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviseConfirm(null)} disabled={Boolean(actionId)}>Cancel</Button>
            <Button onClick={submitRevision} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePenLine className="h-4 w-4 mr-2" />}Revise offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend / extend expired offer */}
      <Dialog open={Boolean(reopen)} onOpenChange={(open) => { if (!open) setReopen(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend expired offer</DialogTitle>
            <DialogDescription>
              Email the same offer letter to {displayCandidateName(reopen?.candidate?.candidate_name) || 'the candidate'} again with a fresh acceptance link and a new deadline. Their old link stops working.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reopen-date">New offer deadline</Label>
            <Input id="reopen-date" type="date" value={reopen?.date || ''} onChange={(event) => setReopen((current) => ({ ...current, date: event.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopen(null)}>Cancel</Button>
            <Button onClick={submitReopen} disabled={Boolean(actionId)}>{actionId ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Resend offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EmployerLayout>
  );
}
